import { SearchContext } from '@giphy/react-components'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { InView } from 'react-intersection-observer'
import useDebounce from 'react-use/lib/useDebounce'
import { MARGIN } from '../../constants'
import { AppContext } from '../../context'
import { textBubble } from '../../img/images'
import {
    Carousel,
    Category,
    Container,
    NavigateBack,
    NavigateForward,
    TextPill,
    TrendingSearchPillContainer,
} from './sc'

export const findCategoryIndex = (searchTerm: string, categories: string[]) =>
    categories.map((c) => c.toLowerCase()).indexOf(searchTerm.toLowerCase())

export const TRENDING = 'Trending'

export const ThinArrow = ({ color = 'white', className = '' }) => (
    <svg
        width="15px"
        height="15px"
        viewBox="0 0 15 30"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
    >
        <g stroke="none" fill="none">
            <g transform="translate(-1017.000000, -82.000000)" fill={color}>
                <g>
                    <g transform="translate(1.000000, 27.000000)">
                        <polygon points="1031 70.0000018 1030.1866 69.0556352 1018.01759 55 1016 56.888726 1027.35562 70.0000018 1016 83.111274 1018.01759 85 1030.1866 70.9443648" />
                    </g>
                </g>
            </g>
        </g>
    </svg>
)

const trimFirstCategory = (category: string, searchTerm: string, index: number) => {
    if (category === TRENDING) return TRENDING
    if (category === searchTerm) return category
    const cutOffLongWordsAt = 8
    if (category.length > cutOffLongWordsAt) {
        // if we're the first category, only show the last cutOffLongWordsAt characters
        if (index === 0) return category.slice(-cutOffLongWordsAt)
        // if we're the last category, only show the first cutOffLongWordsAt characters
        // if (index === numberToDisplay - 1) return category.slice(0, cutOffLongWordsAt)
    }
    return category
}

function usePrevious<T>(value: T) {
    const store = useRef<T[]>([])
    if (store.current[0] !== value) {
        store.current.unshift(value)
        store.current = store.current.slice(0, 2)
    }
    return store.current[1]
}

const Categories = ({ categories, searchTerm }: { categories: string[]; searchTerm: string }) => {
    const {
        displayGif,
        config: { width: appWidth },
        savedTrendingState,
        searchMode,
        setSavedTrendingState,
        setSearchMode,
    } = useContext(AppContext)

    const { setSearch, setActiveChannel, activeChannel } = useContext(SearchContext)

    const currentCategory = categories[findCategoryIndex(searchTerm, categories)]

    // the last item to be displayed
    const lastCategoryDisplayed = useRef<number>(0)
    // the current set
    const currentPage = useRef<number>(0)
    const [pageCategories, setPageCategories] = useState(categories)
    // ui transitions (maybe add this back)
    // const [fadeOut, setFadeout] = useState<boolean>(false)
    // the end of the list
    const [lastPage, setLastPage] = useState(-1)

    const containerWidth = appWidth - 2 * MARGIN

    const previousSearchTerm = usePrevious<string>(searchTerm)

    const isFirstPage = pageCategories[0] === categories[0]
    const isLastPage = lastPage === currentPage.current
    const savePage = () => {
        savedTrendingState[currentPage.current] = pageCategories
        setSavedTrendingState([...savedTrendingState])
    }

    const isSaved = !!savedTrendingState[currentPage.current]

    // save after render
    useDebounce(savePage, 1000, [pageCategories])

    // pagination and animations
    const navigate = async (nextPage: number) => {
        if (currentPage.current !== nextPage) {
            // set the current page
            currentPage.current = nextPage
            // we save them as we render them, so we can go backwwards
            const nextCategories = savedTrendingState[nextPage] || categories.slice(lastCategoryDisplayed.current)
            setPageCategories(nextCategories)
        }
    }

    useEffect(() => {
        if (searchTerm === '' && previousSearchTerm !== '') {
            // navigate to Trending when search term is ''
            navigate(0)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previousSearchTerm, searchTerm])

    useEffect(() => {
        if (searchTerm) {
            for (let i = savedTrendingState.length - 1; i >= 0; i--) {
                const isTheOne = (term: string, index: number) => index !== 0 && term === searchTerm
                if (savedTrendingState[i].some(isTheOne)) {
                    if (currentPage.current !== i) {
                        navigate(i)
                    } else {
                        // last item jump to next page
                        const actualIndex = categories.indexOf(searchTerm)
                        if (lastCategoryDisplayed.current + 1 === actualIndex && !isLastPage) {
                            navigate(i + 1)
                        }
                    }
                    break
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm])

    return (
        <Container hide={!!displayGif || searchMode === 'emoji' || searchMode === 'text' || searchMode === 'videos'}>
            {/* hide items so animation replays when going detail and back */}
            {!displayGif && (
                <Carousel width={containerWidth}>
                    {pageCategories.map((category, index) => {
                        const isText = category === searchTerm && index === 0 && category !== TRENDING
                        return (
                            <Category
                                className="no-animation-in-percy"
                                delay={index * 0.08}
                                key={category + index}
                                onClick={() => {
                                    if (isSaved) {
                                        setActiveChannel(undefined)
                                        if (isText) {
                                            setSearchMode('text')
                                        }
                                        setSearch(category)
                                    }
                                }}
                            >
                                <TrendingSearchPillContainer
                                    isActive={
                                        !activeChannel &&
                                        (currentCategory === category || (category === TRENDING && searchTerm === ''))
                                    }
                                    isText={isText}
                                >
                                    {isText && <TextPill src={textBubble} />}

                                    <InView
                                        as="span"
                                        onChange={(inView, entry) => {
                                            if (inView) {
                                                const actualIndex = categories.indexOf(category)
                                                if (
                                                    entry.intersectionRatio === 1 &&
                                                    lastCategoryDisplayed.current < actualIndex
                                                ) {
                                                    lastCategoryDisplayed.current = actualIndex
                                                    if (actualIndex === categories.length - 1) {
                                                        setLastPage(currentPage.current)
                                                    }
                                                } else if (index !== 0) {
                                                    savePage()
                                                }
                                            }
                                        }}
                                    >
                                        {trimFirstCategory(category, searchTerm, index)}&nbsp;
                                    </InView>
                                </TrendingSearchPillContainer>
                            </Category>
                        )
                    })}
                </Carousel>
            )}
            {!isFirstPage && (
                <NavigateBack
                    onClick={() => {
                        if (isSaved) navigate(currentPage.current - 1)
                    }}
                >
                    <ThinArrow />
                </NavigateBack>
            )}
            {!isLastPage && (
                <NavigateForward
                    onClick={() => {
                        if (isSaved) navigate(currentPage.current + 1)
                    }}
                >
                    <ThinArrow />
                </NavigateForward>
            )}
        </Container>
    )
}

const EnsureCategories = () => {
    // suggestions....
    let { categories, savedTrendingSearchTerms } = useContext(AppContext)
    const { term } = useContext(SearchContext)

    if (term && categories.indexOf(term) === -1) {
        categories = categories.filter((val) => val.toLowerCase() !== term.toLowerCase())
        // add the term which will be the Text CTA
        categories.unshift(term)
    }
    // wait until we've fetched trending search terms before rendering
    return savedTrendingSearchTerms.length > 0 ? (
        <Categories categories={categories} searchTerm={term} key={term} />
    ) : null
}
export default EnsureCategories
